import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { ConsolidatedReportModel, statusTagVariant } from '../consolidatedReportModel';
import { getStatusDisplayForItem } from '../fbsFindingStatuses';
import { ConsolidatedSystemBlock, FbsReportItem, FbsReportOrgan } from '../fullBodyScanReportUtils';
import { PdfCard, PdfFieldBlock, PdfKeyValue, PdfStatusTag } from './PdfPrimitives';
import { pdfStyles } from './pdfTheme';

const formatConditionLabel = (item: FbsReportItem): string =>
  item.condition || item.description || '-';

const ConditionCell: React.FC<{ item: FbsReportItem }> = ({ item }) => {
  const condition = formatConditionLabel(item);
  const showDescHint =
    !!item.description && !!item.condition && item.description !== item.condition;

  return (
    <View style={pdfStyles.conditionCol}>
      <Text style={pdfStyles.conditionText}>{condition}</Text>
      {showDescHint ? (
        <Text style={pdfStyles.descHint}>({item.description})</Text>
      ) : null}
    </View>
  );
};

const StatusCell: React.FC<{ item: FbsReportItem; bookingUsesV2: boolean }> = ({
  item,
  bookingUsesV2
}) => {
  const rowStatus = getStatusDisplayForItem(item, bookingUsesV2).label;

  return (
    <View style={pdfStyles.statusCol}>
      <PdfStatusTag
        label={rowStatus}
        variant={statusTagVariant(rowStatus, bookingUsesV2)}
      />
      {item.detailedNote?.trim() ? (
        <Text style={pdfStyles.itemNote}>{item.detailedNote}</Text>
      ) : null}
    </View>
  );
};

const ConsolidatedFindingRow: React.FC<{ item: FbsReportItem; bookingUsesV2: boolean }> = ({
  item,
  bookingUsesV2
}) => (
  <View wrap={false} style={pdfStyles.innerRow}>
    <ConditionCell item={item} />
    <StatusCell item={item} bookingUsesV2={bookingUsesV2} />
  </View>
);

/** Matches web `.consolidated-organ-block` + `.consolidated-outer-card`. */
export const ConsolidatedOrganPdfSection: React.FC<{
  organKey: string;
  organData: FbsReportOrgan;
  bookingUsesV2: boolean;
}> = ({ organKey, organData, bookingUsesV2 }) => {
  if (!organData.items?.length) return null;

  return (
    <View style={pdfStyles.organBlock}>
      <Text style={pdfStyles.organTitle}>{organData.name || organKey}</Text>
      <View style={pdfStyles.outerCard}>
        <View style={pdfStyles.rowHead}>
          <Text style={pdfStyles.rowHeadCellLeft}>Conditions</Text>
          <Text style={pdfStyles.rowHeadCellRight}>Status</Text>
        </View>
        {organData.items.map((item, index) => (
          <ConsolidatedFindingRow
            key={item.id || `${organKey}-${index}`}
            item={item}
            bookingUsesV2={bookingUsesV2}
          />
        ))}
      </View>
    </View>
  );
};

/** Matches web `.consolidated-system-shell`. */
export const ConsolidatedSystemPdfSection: React.FC<{
  system: ConsolidatedSystemBlock;
  bookingUsesV2: boolean;
}> = ({ system, bookingUsesV2 }) => (
  <View style={pdfStyles.systemShell}>
    <Text style={pdfStyles.systemName}>{system.name}</Text>

    <View wrap={false} style={pdfStyles.systemMetaRow}>
      <View style={pdfStyles.systemMetaStatus}>
        <Text style={pdfStyles.fieldLabel}>System status</Text>
        <PdfStatusTag
          label={system.status}
          variant={statusTagVariant(system.status, bookingUsesV2)}
          context="system"
        />
      </View>
      <View style={pdfStyles.systemNotesBox}>
        <Text style={pdfStyles.fieldLabel}>System notes</Text>
        <Text style={pdfStyles.systemNotesText}>
          {system.systemNote?.trim() || 'No notes available for this system.'}
        </Text>
      </View>
    </View>

    {Object.entries(system.organs).map(([organKey, organData]) => (
      <ConsolidatedOrganPdfSection
        key={organKey}
        organKey={organKey}
        organData={organData}
        bookingUsesV2={bookingUsesV2}
      />
    ))}
  </View>
);

export const ConsolidatedReportHeaderPdf: React.FC<{ model: ConsolidatedReportModel }> = ({
  model
}) => (
  <>
    <Text style={pdfStyles.title}>Full Body Scan — Consolidated Report</Text>

    <PdfCard title="Patient booking information">
      <View style={pdfStyles.bookingColumns}>
        <View style={pdfStyles.bookingCol}>
          <PdfKeyValue label="Patient name" value={model.patientName} />
          <PdfKeyValue label="Scan date & time" value={model.scanDateTimeLine} />
          <PdfKeyValue label="Date of birth" value={model.dateOfBirth} />
          <PdfKeyValue label="Booking ID" value={model.bookingId} />
        </View>
        <View style={pdfStyles.bookingColRight}>
          <PdfKeyValue label="Gender" value={model.gender} />
          <PdfKeyValue label="Radiologist" value={model.radiologist} />
          <PdfKeyValue label="GP" value={model.gp} />
        </View>
      </View>
    </PdfCard>

    <PdfCard title="Conclusions and Recommendations">
      {model.followUpLabel ? (
        <Text style={pdfStyles.subheading}>{model.followUpLabel}</Text>
      ) : null}
      {model.conclusionBody ? (
        <Text style={pdfStyles.bodyText}>{model.conclusionBody}</Text>
      ) : (
        <Text style={pdfStyles.mutedText}>{model.conclusionEmptyMessage}</Text>
      )}
    </PdfCard>

    <PdfCard title="Radiologist notes">
      <View style={pdfStyles.notesStack}>
        <PdfFieldBlock label="Technique" value={model.technique} />
        <PdfFieldBlock label="Scan Technical Satisfactory" value={model.scanTechnical} />
        <PdfFieldBlock label="Scan Adequate Coverage" value={model.scanCoverage} />
        <PdfFieldBlock label="Additional comments" value={model.additionalComments} />
      </View>
    </PdfCard>
  </>
);
